import { z } from 'zod';

export const TASK_REALTIME_SUBSCRIPTION = '/user/queue/task-events' as const;
const TASK_REALTIME_SCHEMA_VERSION = 3 as const;

const TASK_UPSERT_EVENTS = ['create', 'assignment', 'update'] as const;
const TASK_REMOVE_EVENTS = ['complete', 'delete'] as const;

type TaskRealtimeUpsertEventType = (typeof TASK_UPSERT_EVENTS)[number];
type TaskRealtimeRemoveEventType = (typeof TASK_REMOVE_EVENTS)[number];

interface TaskRealtimeUpsert {
  schemaVersion: typeof TASK_REALTIME_SCHEMA_VERSION;
  type: 'TASK_UPSERT';
  taskId: string;
  eventType: TaskRealtimeUpsertEventType;
}

interface TaskRealtimeRemove {
  schemaVersion: typeof TASK_REALTIME_SCHEMA_VERSION;
  type: 'TASK_REMOVE';
  taskId: string;
  eventType: TaskRealtimeRemoveEventType;
}

export type TaskRealtimeTaskEvent = TaskRealtimeUpsert | TaskRealtimeRemove;

interface TaskRealtimeInvalidation {
  schemaVersion: typeof TASK_REALTIME_SCHEMA_VERSION;
  type: 'TASKS_INVALIDATED';
}

export type TaskRealtimeEnvelope = TaskRealtimeTaskEvent | TaskRealtimeInvalidation;

const taskRealtimeEnvelopeInputSchema = z.discriminatedUnion('type', [
  z.strictObject({
    schemaVersion: z.number(),
    type: z.literal('TASKS_INVALIDATED'),
  }),
  z.strictObject({
    schemaVersion: z.number(),
    type: z.literal('TASK_UPSERT'),
    taskId: z.string(),
    eventType: z.enum(TASK_UPSERT_EVENTS),
  }),
  z.strictObject({
    schemaVersion: z.number(),
    type: z.literal('TASK_REMOVE'),
    taskId: z.string(),
    eventType: z.enum(TASK_REMOVE_EVENTS),
  }),
]);

function parseTaskRealtimeEnvelope<T>(value: T): TaskRealtimeEnvelope | null {
  const parsed = taskRealtimeEnvelopeInputSchema.safeParse(value);
  if (!parsed.success || parsed.data.schemaVersion !== TASK_REALTIME_SCHEMA_VERSION) {
    return null;
  }

  if (parsed.data.type === 'TASKS_INVALIDATED') {
    return {
      schemaVersion: TASK_REALTIME_SCHEMA_VERSION,
      type: 'TASKS_INVALIDATED',
    };
  }

  if (parsed.data.taskId.trim().length === 0) {
    return null;
  }

  if (parsed.data.type === 'TASK_UPSERT') {
    return {
      schemaVersion: TASK_REALTIME_SCHEMA_VERSION,
      type: 'TASK_UPSERT',
      taskId: parsed.data.taskId,
      eventType: parsed.data.eventType,
    };
  }

  return {
    schemaVersion: TASK_REALTIME_SCHEMA_VERSION,
    type: 'TASK_REMOVE',
    taskId: parsed.data.taskId,
    eventType: parsed.data.eventType,
  };
}

export function parseTaskRealtimeEnvelopePayload(payload: string): TaskRealtimeEnvelope | null {
  try {
    return parseTaskRealtimeEnvelope(JSON.parse(payload));
  } catch {
    return null;
  }
}
